import { BaseHandler, GameConfig } from "@/lib/BaseHandler";
import { CardItem, OPItem } from "@/interfaces/CardItem";
import {
  getCardImage,
  getCardData,
  getAltArts,
  getAACardId,
  getAACardIdData,
  getAAFromCardId,
  cleanCardData,
} from "@/lib/games/onePiece/getCardDataLocal";
import { getLeaderData } from "@/lib/leaderManager";
import { getCardLink2 } from "@/lib/getCardLink";
import { getDonImage } from "@/lib/getCardImage";
import { getOpId } from "@/lib/tcgplayer/getCardPrice";
import { getTCGPlayerCardImage } from "@/lib/tcgplayer/tcgplayer_helper";
import { replaceKeywords } from "@/components/keywords";
import { DeckCardsMap } from "@/lib/games/onePiece/DeckCommonCardsFilter";
import { sortCards as opSortCards } from "@/lib/games/onePiece/deckbuilderSortCards";
import { CardModalData } from "@/interfaces/GameHandler";
import { CDN } from "@/lib/cdn";

// Decklist sorting function
function sortDecklist(cardList: string, leader?: string): string {
  const cardEntries = cardList.split(";").map((entry) => {
    const [quantity, cardId] = entry.split("x");
    return { quantity: parseInt(quantity), cardId };
  });

  const sortedEntries = cardEntries.sort((a, b) => {
    const aCard = getCardData(a.cardId);
    const bCard = getCardData(b.cardId);

    if (!aCard || !bCard) return 0;

    const aOPCard = aCard as OPItem;
    const bOPCard = bCard as OPItem;

    const typeOrder = { Leader: 1, Character: 2, Event: 3, Stage: 4 };
    const typeComparison =
      typeOrder[aOPCard.type as keyof typeof typeOrder] -
      typeOrder[bOPCard.type as keyof typeof typeOrder];
    if (typeComparison !== 0) return typeComparison;

    const costComparison = (aOPCard.cost ?? 0) - (bOPCard.cost ?? 0);
    if (costComparison !== 0) return costComparison;

    const powerComparison = (aOPCard.power ?? 0) - (bOPCard.power ?? 0);
    if (powerComparison !== 0) return powerComparison;

    return aOPCard.number.localeCompare(bOPCard.number);
  });

  return sortedEntries
    .map((entry) => `${entry.quantity}x${entry.cardId}`)
    .join(";");
}

// Filter cards function
function filterCards(
  cards: CardItem[],
  filters: any,
  defaultFilters: any,
  deckCardsMap: DeckCardsMap,
): CardItem[] {
  const filteredCards: OPItem[] = [];

  for (const card of cards) {
    const opCard = card as OPItem;
    let shouldAdd = true;

    if (filters.deckCommonCards && filters.deckCommonCards !== "") {
      const commonCards = deckCardsMap[filters.deckCommonCards].cards;
      if (!commonCards.includes(opCard.number)) {
        shouldAdd = false;
      }
    }

    if (filters.search !== "") {
      if (shouldAdd && filters.searchSelect) {
        if (filters.searchSelect.has("name")) {
          shouldAdd = opCard.name
            .toLowerCase()
            .includes(filters.search.toLowerCase());
        } else if (filters.searchSelect.has("effect")) {
          shouldAdd = opCard.description
            .toLowerCase()
            .includes(filters.search.toLowerCase());
        } else if (filters.searchSelect.has("trigger")) {
          shouldAdd = false;
          if (opCard.trigger) {
            shouldAdd = opCard.trigger
              .toLowerCase()
              .includes(filters.search.toLowerCase());
          }
        } else if (filters.searchSelect.has("type")) {
          shouldAdd = opCard.subtype
            .toLowerCase()
            .includes(filters.search.toLowerCase());
        } else {
          shouldAdd =
            opCard.name.toLowerCase().includes(filters.search.toLowerCase()) ||
            opCard.description
              .toLowerCase()
              .includes(filters.search.toLowerCase()) ||
            opCard.subtype
              .toLowerCase()
              .includes(filters.search.toLowerCase()) ||
            opCard.number.toLowerCase().includes(filters.search.toLowerCase());
          if (opCard.trigger) {
            shouldAdd =
              shouldAdd ||
              opCard.trigger
                .toLowerCase()
                .includes(filters.search.toLowerCase());
          }
        }
      }
    }

    if (filters.color && filters.color.size !== 0) {
      const colorsToSearch = Array.from(filters.color);
      const curColors = opCard.color
        .split("/")
        .map((element: string) => element.toLowerCase());
      shouldAdd =
        shouldAdd &&
        curColors.some((color: string) => colorsToSearch.includes(color));
    }

    if (filters.cost && filters.cost.size !== 0) {
      const curCost = String(opCard.cost);
      shouldAdd = shouldAdd && filters.cost.has(curCost);
    }

    if (filters.power && filters.power.size !== 0) {
      let curPower = String(opCard.power);
      if (curPower === "-") {
        curPower = "0";
      }
      shouldAdd = shouldAdd && filters.power.has(curPower);
    }

    if (filters.type && filters.type.size !== 0) {
      shouldAdd = shouldAdd && filters.type.has(opCard.type);
    }

    if (filters.counter && filters.counter.size !== 0) {
      let curCounter = String(opCard.counter);
      if (curCounter === "-" || curCounter === null || curCounter === "null") {
        curCounter = "0";
      }
      shouldAdd = shouldAdd && filters.counter.has(curCounter);
    }

    if (filters.attribute && filters.attribute.size !== 0) {
      let curAttribute = String(opCard.attribute);
      if (curAttribute.includes("/")) {
        curAttribute = curAttribute.replace("/", "_");
      }
      shouldAdd =
        shouldAdd && filters.attribute.has(curAttribute.toLowerCase());
    }

    if (filters.format && filters.format.size !== 0) {
      const vals: string[] = Array.from(filters.format) as string[];
      if (vals.length === 1) {
        shouldAdd = shouldAdd && opCard.number.startsWith(vals[0]);
      }
    }

    if (shouldAdd) {
      filteredCards.push(opCard);
    }
  }

  return filteredCards;
}

const opConfig: GameConfig = {
  cdnBaseUrl: CDN,
  setImagePath: "/games/one-piece/images/set-squares/",
  hasLeader: true,

  cardDataFunctions: {
    getCardData,
    getCardImage: (cardId: string, size?: string, src?: any) =>
      getCardImage(
        cardId,
        size as "original" | "316x440" | "158x220" | "90x125",
        src,
      ),
    getAltArts,
    getAACardId,
    getAACardIdData,
    getAAFromCardId,
    cleanCardData,
  },

  // getLeaderData removed - use useLeaders() hook in components instead

  getTcgPlayerCardData: (tcgplayerId: string) => {
    const OPId = getOpId(tcgplayerId);
    if (OPId) {
      return getCardData(OPId);
    }
    return undefined;
  },

  getDefaultFilters: () => ({
    searchSelect: new Set(["name"]),
    search: "",
    text: "",
    color: new Set(),
    cost: new Set(),
    power: new Set(),
    type: new Set(),
    counter: new Set(),
    attribute: new Set(),
    format: new Set(),
    deckCommonCards: "",
    showAAs: false,
  }),

  filterCards,
  sortCards: (a: CardItem, b: CardItem) =>
    opSortCards(a as OPItem, b as OPItem),
  sortDecklist,

  getCardType: (card: CardItem) => (card as OPItem).subtype,
  getCardEffect: (card: CardItem) =>
    replaceKeywords((card as OPItem).description ?? ""),
  getCardTrigger: (card: CardItem) => {
    const trigger = (card as OPItem).trigger;
    return trigger ? replaceKeywords(trigger) : [];
  },
  getCardLink: (card: CardItem, tcgplayerId?: string | null) =>
    getCardLink2(card as OPItem, tcgplayerId || undefined),

  getCost: (card: CardItem) => (card as OPItem).cost ?? 0,
  getColor: (card: CardItem) => (card as OPItem).color,
  getPower: (card: CardItem) => (card as OPItem).power ?? 0,
  isLeader: (card: CardItem) => (card as OPItem).type === "Leader",
};

export class OPHandler extends BaseHandler {
  constructor() {
    super(opConfig);
  }

  // Override getCardModalData for DON card special handling
  /** DON cards are resources, not decklist entries, so they are not exported. */
  getExportCardId(cardId: string): string | null {
    return cardId.includes("DON") ? null : cardId;
  }

  getSet(card: CardItem): string {
    return (card as OPItem).groupId || "";
  }

  getTraits(card: CardItem): string[] {
    const subtype = (card as OPItem).subtype;
    return subtype ? subtype.split("/").filter(Boolean) : [];
  }

  getCardModalData(): CardModalData {
    const baseModalData = super.getCardModalData();
    return {
      ...baseModalData,
      getCardImage: (cardId: string, tcgplayerId?: string | null) => {
        if (tcgplayerId) {
          return getTCGPlayerCardImage(tcgplayerId);
        }
        if (cardId.includes("DON")) {
          return getDonImage(cardId);
        }
        return getCardImage(cardId);
      },
    };
  }
}
